import { useClientConfig, type ClientConfig } from '@providers/ClientConfigProvider';
import { StatsigClient } from '@statsig/js-client';
import { isBot } from '@utils/utils';
import { isNavigatorDefined, isWindowDefined } from '@utils/validations';
import { useCallback } from 'react';

const botStableID = 'bot_user';
const serverStableID = 'server_user';

let statsigClient: StatsigClient | null = null;
let stableID = serverStableID;

type StatsigConfig = Pick<ClientConfig, 'STATSIG_CLIENT_TOKEN' | 'IS_STAGING' | 'STATSIG_ENVIRONMENT'>;

async function getStatsigClient(config: StatsigConfig) {
  const anonymousId = isWindowDefined && window?.anonymousId;
  const userAgent = isNavigatorDefined && navigator.userAgent;
  if (statsigClient) {
    if (anonymousId && stableID !== anonymousId && !isBot(userAgent || '')) {
      stableID = anonymousId;
      await statsigClient?.updateUserAsync({ customIDs: { stableID } });
    }
    return statsigClient;
  }
  const clientKey = config.STATSIG_CLIENT_TOKEN;
  const isStaging = config.IS_STAGING === 'true';
  const tier = isStaging ? 'staging' : config.STATSIG_ENVIRONMENT;
  stableID = isBot(userAgent || '') ? botStableID : anonymousId || serverStableID;
  statsigClient = new StatsigClient(clientKey, { customIDs: { stableID } }, { environment: { tier } });
  await statsigClient.initializeAsync();
  return statsigClient;
}

export async function getStatsigServer(config: StatsigConfig, anonymousId: string) {
  const clientKey = config.STATSIG_CLIENT_TOKEN;
  const isStaging = config.IS_STAGING === 'true';
  const tier = isStaging ? 'staging' : config.STATSIG_ENVIRONMENT;
  stableID = anonymousId || serverStableID;
  const client = new StatsigClient(clientKey, { customIDs: { stableID } }, { environment: { tier } });
  await client.initializeAsync();
  return client;
}

export default async function getStatsigExperimentValue<T>(
  config: StatsigConfig,
  name: string,
  param: string,
  fallback?: T,
  client: StatsigClient | null = null,
) {
  try {
    const sClient = client ?? (await getStatsigClient(config));
    return sClient?.getExperiment(name).get(param);
  } catch (error) {
    console.error(error);
    return fallback;
  }
}

export async function getStatsigGateValue(config: StatsigConfig, name: string) {
  try {
    const client = await getStatsigClient(config);
    return client.checkGate(name);
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * Hook to get a function that can fetch Statsig experiment values.
 * Uses the client config from context.
 */
export function useStatsigExperiment() {
  const config = useClientConfig();

  return useCallback(
    async <T>(name: string, param: string, fallback?: T) => {
      return getStatsigExperimentValue(config, name, param, fallback);
    },
    [config],
  );
}

/**
 * Hook to get a function that can check Statsig gates.
 * Uses the client config from context.
 */
export function useStatsigGate() {
  const config = useClientConfig();

  return useCallback(
    async (name: string) => {
      return getStatsigGateValue(config, name);
    },
    [config],
  );
}
